import type { CpuInfo, StackAmount } from "../../shared/api.js";
import { Page } from "../Page.js";
import { jobTable } from "../components/JobTable.js";
import { h, patch } from "../dom.js";
import { amount, compact, stackName } from "../format.js";
import { stackIcon } from "../components/StackIcon.js";

/** One card per crafting CPU, then the recent jobs. */
export class CraftsPage extends Page {
  readonly title = "Crafts";
  private readonly grid = h("div", { class: "cpu-grid" });
  private readonly jobs = h("div");
  private readonly header = h("p", { class: "muted" });

  mount(): void {
    this.el.append(h("h2", null, "Crafting CPUs"), this.header, this.grid, h("h2", null, "Recent jobs"), this.jobs);
    this.listen("cpus", () => this.renderCpus());
    this.listen("settings", () => this.renderCpus());
    this.listen("jobs", () => this.renderJobs());
    this.renderCpus();
    this.renderJobs();
  }

  private renderCpus(): void {
    const cpus = this.ctx.live.cpus;
    const busy = cpus.filter((c) => c.busy).length;
    const reserved = this.ctx.live.settings?.reservedCpus ?? 0;
    this.header.textContent = cpus.length
      ? `${busy} of ${cpus.length} busy. ${reserved} kept free for manual crafts.`
      : this.ctx.live.link?.connected
        ? "The Butler has not reported any crafting CPU."
        : "The game is not connected.";
    patch(this.grid, ...cpus.map((cpu) => this.card(cpu)));
  }

  private list(title: string, items: StackAmount[]): HTMLElement | null {
    if (!items.length) return null;
    return h(
      "details",
      { class: "cpu-items" },
      h("summary", null, `${title} (${items.length})`),
      h("ul", null, ...items.slice(0, 50).map((i) => h("li", null, stackIcon(i.stack), `${amount(i.amount, i.stack.type)} × ${stackName(i.stack)}`))),
    );
  }

  private card(cpu: CpuInfo): HTMLElement {
    const job = cpu.job ? this.ctx.live.jobs.get(cpu.job) : undefined;
    return h(
      "article",
      { key: cpu.name, class: `cpu-card ${cpu.busy ? "busy" : "free"}` },
      h("header", null, h("strong", null, cpu.name.startsWith("#") ? `Unnamed CPU ${cpu.name}` : cpu.name), h("span", { class: `badge ${cpu.busy ? "crafting" : "ok"}` }, cpu.busy ? "Busy" : "Free")),
      h("div", { class: "muted small" }, `${compact(cpu.storage)} bytes · ${cpu.coprocessors} co-processors`),
      cpu.output
        ? h("div", { class: "cpu-output" }, stackIcon(cpu.output.stack, "lg"), h("strong", null, `${amount(cpu.output.amount, cpu.output.stack.type)} × ${stackName(cpu.output.stack)}`))
        : h("div", { class: "muted cpu-output" }, cpu.busy ? "Crafting" : "Idle"),
      job ? h("div", { class: "muted small" }, `Job from ${job.origin === "auto" ? "a rule" : job.origin}`) : null,
      this.list("Active", cpu.active),
      this.list("Pending", cpu.pending),
      cpu.busy
        ? h(
            "button",
            {
              class: "small danger",
              onclick: async () => {
                if (!confirm(`Cancel the craft on ${cpu.name}?`)) return;
                await this.attempt(() => this.ctx.api.cancelCpu(cpu.name), "Cancel sent");
              },
            },
            "Cancel craft",
          )
        : null,
    );
  }

  private renderJobs(): void {
    patch(this.jobs, jobTable(this.ctx.live.jobList().slice(0, 100), "No crafts yet."));
  }
}
